import { defineStore } from "pinia";
import DocumentsAPI, { type DocumentResp } from "@/data/api-layer/documentsApi";
import StudiesAPI, { type UserStudyResp } from "@/data/api-layer/studiesApi";
import { useClientSettingsKey } from "@/plugins/useClientSettings";
import { useToastKey } from "@/plugins/useToast";

export interface DocumentByStudy {
   [key: string]: DocumentResp[];
}

const useAccountStore = defineStore("accountStore", () => {
   //Declarations
   // -----------------------------------------
   const clientSettings = inject(useClientSettingsKey);
   const useToast = inject(useToastKey);

   // State
   const documentsResp = ref<DocumentResp[]>([]);
   const userStudiesResp = ref<UserStudyResp[]>([]);

   // Computed
   // -----------------------------------------
   const generalDocs = computed<DocumentResp[]>(() => documentsResp.value.filter((doc) => !doc.study?.id)); // Documents not associated with a study
   const docsByStudyId = computed<DocumentByStudy>(() => {
      // docsByStudyId example:
      // {
      //    "studyId1": [doc1, doc2, doc3],
      //    "studyId2": [doc4, doc5],
      // }
      const studyDocs = documentsResp.value.filter((doc) => doc.study?.id);
      const sortedStudyDocs: DocumentByStudy = {};
      studyDocs.forEach((doc) => {
         const studyId = doc.study?.id;
         if (!sortedStudyDocs[studyId]) {
            sortedStudyDocs[studyId] = [];
         }
         sortedStudyDocs[studyId].push(doc);
      });

      return sortedStudyDocs;
   });
   const studiesWithParticipation = computed<string[]>(() => userStudiesResp.value.map((study) => study.studyId));
   // Legacy studies are studies that the user has participated in, but are not in the current userStudiesResp as in the past a concept of "participation" did not exist
   const legacyStudyIds = computed<string[]>(() =>
      Object.keys(docsByStudyId.value).filter((studyId) => !studiesWithParticipation.value.includes(studyId))
   );

   // Methods
   // -----------------------------------------
   async function setUserStudiesAndDocuments(errorMessage) {
      try {
         userStudiesResp.value = await StudiesAPI.getUserStudies();
         if (clientSettings.userDocuments.isActive) {
            documentsResp.value = await DocumentsAPI.getAll();
         }
      } catch (e) {
         console.error("Error setting user studies and documents", e);
         useToast({ text: errorMessage, type: "error" });
      }
   }

   function $reset() {
      documentsResp.value = [];
      userStudiesResp.value = [];
   }

   return {
      setUserStudiesAndDocuments,
      userStudiesResp,
      documentsResp,
      docsByStudyId,
      generalDocs,
      $reset,
      legacyStudyIds,
   };
});

export { useAccountStore };
